import type { DataProvider } from "ra-core";

import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

// Tenax columns of people and organizations (task 1.5, D-033). Atomic reads
// a record from its summary views, which do not have them: they are read
// from the table and merged into the record shown in the form.
export const CONTACT_TENAX_COLUMNS = [
  "origin",
  "channel",
  "instagram_handle",
  "instagram_followers",
  "fitp_qualification",
  "player_level",
  "home_club_id",
  "coach_contact_id",
  "marketing_consent",
  "consent_at",
  "consent_source",
  "fic_client_id",
] as const;

export const COMPANY_TENAX_COLUMNS = [
  "org_type",
  "region",
  "country_code",
  "language",
  "priority",
  "group_name",
  "courts_count",
  "introduced_by_contact_id",
  "min_order_amount",
  "payment_method",
  "payment_discount_pct",
  "consignment",
  "unsold_exchange_used_at",
  "recontact_month",
  "fic_client_id",
  "origin",
  "channel",
] as const;

// Form-only fields of a person: roles and the clubs where a coach teaches.
// Other links (member_of, referent_of, works_at) are kept as they are.
export interface ContactLink {
  company_id: number;
  relation: string;
  role_title: string | null;
}

export const VIRTUAL_CONTACT_FIELDS = [
  "tenax_roles",
  "tenax_teaches_at",
  "tenax_other_links",
] as const;

const TEACHES_AT = "teaches_at";

const readRow = async (
  table: string,
  id: unknown,
  columns: readonly string[],
) => {
  const { data, error } = await getSupabaseClient()
    .from(table)
    .select(columns.join(", "))
    .eq("id", id)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as Record<string, unknown>;
};

const readContactLinks = async (contactId: unknown) => {
  const client = getSupabaseClient();
  const [roles, links] = await Promise.all([
    client.from("contact_roles").select("role").eq("contact_id", contactId),
    client
      .from("contact_company_links")
      .select("company_id, relation, role_title")
      .eq("contact_id", contactId),
  ]);
  if (roles.error) throw new Error(roles.error.message);
  if (links.error) throw new Error(links.error.message);
  const allLinks = (links.data ?? []) as ContactLink[];
  return {
    tenax_roles: (roles.data ?? []).map((row) => row.role as string),
    tenax_teaches_at: allLinks
      .filter((link) => link.relation === TEACHES_AT)
      .map((link) => link.company_id),
    tenax_other_links: allLinks.filter((link) => link.relation !== TEACHES_AT),
  };
};

// Roles and links in one call (tenax_save_contact_links), only when the form
// sent them: other screens that update a person leave them untouched.
export const toSaveLinksArgs = (
  contactId: unknown,
  data: Record<string, unknown>,
) => {
  if (!("tenax_roles" in data) && !("tenax_teaches_at" in data)) return null;
  const teachesAt = (data.tenax_teaches_at as number[] | undefined) ?? [];
  const otherLinks =
    (data.tenax_other_links as ContactLink[] | undefined) ?? [];
  return {
    p_contact_id: contactId,
    p_roles: (data.tenax_roles as string[] | undefined) ?? [],
    p_links: [
      ...otherLinks,
      ...teachesAt.map((companyId) => ({
        company_id: companyId,
        relation: TEACHES_AT,
        role_title: null,
      })),
    ],
  };
};

// The record to write: no form-only fields, and no consent date, which the
// database sets (D-033).
export const withoutTenaxFormFields = (data: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(data).filter(
      ([key]) =>
        !(VIRTUAL_CONTACT_FIELDS as readonly string[]).includes(key) &&
        key !== "consent_at",
    ),
  );

const saveContactLinks = async (
  contactId: unknown,
  data: Record<string, unknown>,
) => {
  const args = toSaveLinksArgs(contactId, data);
  if (!args) return;
  const { error } = await getSupabaseClient().rpc(
    "tenax_save_contact_links",
    args,
  );
  if (error) throw new Error(error.message);
};

// Organizations of type club, for the clubs of a coach and the home club of
// a player: read from companies, which has org_type, not from Atomic's
// summary view, which does not (D-033). Read-only.
export const CLUBS_RESOURCE = "tenax_clubs";

const listClubs = async (params: {
  pagination?: { page: number; perPage: number };
  filter?: { q?: string };
}) => {
  const page = params.pagination?.page ?? 1;
  const perPage = params.pagination?.perPage ?? 25;
  let query = getSupabaseClient()
    .from("companies")
    .select("id, name", { count: "exact" })
    .eq("org_type", "club")
    .order("name", { ascending: true })
    .range((page - 1) * perPage, page * perPage - 1);
  const search = params.filter?.q?.trim();
  if (search) query = query.ilike("name", `%${search}%`);
  const { data, error, count } = await query;
  if (error) throw new Error(error.message);
  return { data: data ?? [], total: count ?? 0 };
};

const getManyCompanies = async (ids: unknown[]) => {
  const { data, error } = await getSupabaseClient()
    .from("companies")
    .select("id, name")
    .in("id", ids as number[]);
  if (error) throw new Error(error.message);
  return { data: data ?? [] };
};

// TENAX: Tenax fields of contacts and companies (task 1.5, D-033).
export const withTenaxPeople = <T extends DataProvider>(
  dataProvider: T,
): T => ({
  ...dataProvider,
  async getList(resource, params) {
    if (resource === CLUBS_RESOURCE) return listClubs(params) as never;
    return dataProvider.getList(resource, params);
  },
  async getMany(resource, params) {
    if (resource === CLUBS_RESOURCE)
      return getManyCompanies(params.ids) as never;
    return dataProvider.getMany(resource, params);
  },
  async getOne(resource, params) {
    const result = await dataProvider.getOne(resource, params);
    if (resource === "contacts") {
      const [columns, links] = await Promise.all([
        readRow("contacts", params.id, CONTACT_TENAX_COLUMNS),
        readContactLinks(params.id),
      ]);
      return { data: { ...result.data, ...columns, ...links } };
    }
    if (resource === "companies") {
      const columns = await readRow(
        "companies",
        params.id,
        COMPANY_TENAX_COLUMNS,
      );
      return { data: { ...result.data, ...columns } };
    }
    return result;
  },
  async create(resource, params) {
    if (resource !== "contacts") return dataProvider.create(resource, params);
    const result = await dataProvider.create(resource, {
      ...params,
      data: withoutTenaxFormFields(params.data),
    });
    await saveContactLinks(result.data.id, params.data);
    return result;
  },
  async update(resource, params) {
    if (resource !== "contacts") return dataProvider.update(resource, params);
    const result = await dataProvider.update(resource, {
      ...params,
      data: withoutTenaxFormFields(params.data),
    });
    await saveContactLinks(params.id, params.data);
    return result;
  },
});
